import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import html2pdf from "html2pdf.js";

interface SafetyPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
}

export function SafetyPlanDialog({ open, onOpenChange, orgId }: SafetyPlanDialogProps) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    property_name: "",
    roof_access_method: "",
    fall_protection_method: "",
    crew_size: "",
    equipment_list: "",
    hazards_identified: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
  });

  const generatePlanHTML = () => {
    const equipmentArray = formData.equipment_list.split(',').map(s => s.trim()).filter(Boolean);
    const hazardsArray = formData.hazards_identified.split(',').map(s => s.trim()).filter(Boolean);
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>OSHA Safety Plan - ${formData.property_name}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
          h1 { color: #2563eb; border-bottom: 3px solid #2563eb; padding-bottom: 10px; }
          h2 { color: #1e40af; margin-top: 30px; }
          .section { margin: 20px 0; }
          .label { font-weight: bold; color: #374151; }
          ul { margin: 10px 0; padding-left: 20px; }
          .footer { margin-top: 50px; padding-top: 20px; border-top: 2px solid #e5e7eb; font-size: 12px; color: #6b7280; }
        </style>
      </head>
      <body>
        <h1>OSHA SAFETY PLAN</h1>
        <div class="section">
          <p><span class="label">Project:</span> ${formData.property_name}</p>
          <p><span class="label">Date Generated:</span> ${new Date().toLocaleDateString()}</p>
          <p><span class="label">Crew Size:</span> ${formData.crew_size} personnel</p>
        </div>

        <h2>1. ROOF ACCESS METHOD</h2>
        <div class="section">
          <p>${formData.roof_access_method || 'Not specified'}</p>
        </div>

        <h2>2. FALL PROTECTION PLAN</h2>
        <div class="section">
          <p><span class="label">Method:</span> ${formData.fall_protection_method || 'Not specified'}</p>
          <p>All personnel must be trained and certified in fall protection systems. Harnesses must be inspected daily before use.</p>
        </div>

        <h2>3. EQUIPMENT & TOOLS</h2>
        <div class="section">
          <ul>
            ${equipmentArray.map(item => `<li>${item}</li>`).join('')}
          </ul>
        </div>

        <h2>4. IDENTIFIED HAZARDS</h2>
        <div class="section">
          <ul>
            ${hazardsArray.map(hazard => `<li>${hazard}</li>`).join('')}
          </ul>
        </div>

        <h2>5. EMERGENCY PROCEDURES</h2>
        <div class="section">
          <p><span class="label">Emergency Contact:</span> ${formData.emergency_contact_name || 'Not specified'}</p>
          <p><span class="label">Phone:</span> ${formData.emergency_contact_phone || 'Not specified'}</p>
          <p><span class="label">Emergency Services:</span> Call 911</p>
          <p>All crew members must know the location of the nearest hospital and have a clear evacuation route.</p>
        </div>

        <h2>6. SAFETY REQUIREMENTS</h2>
        <div class="section">
          <ul>
            <li>Hard hats must be worn at all times</li>
            <li>Safety glasses required for all work</li>
            <li>Steel-toed boots mandatory</li>
            <li>High-visibility vests when working near active areas</li>
            <li>Weather monitoring - work stops in high winds or lightning</li>
          </ul>
        </div>

        <div class="footer">
          <p>This safety plan must be reviewed with all crew members before work begins. Compliance with all OSHA regulations is mandatory.</p>
          <p>Generated by RoofGenius AI Platform</p>
        </div>
      </body>
      </html>
    `;
  };

  const handleGenerateAndSave = async () => {
    if (!formData.property_name || !formData.crew_size) {
      toast.error("Please fill in required fields: Property Name and Crew Size");
      return;
    }

    setSaving(true);
    const loadingToast = toast.loading("Generating OSHA Safety Plan PDF...");
    
    try {
      const planHTML = generatePlanHTML();
      
      // Generate PDF
      const element = document.createElement('div');
      element.innerHTML = planHTML;
      
      const opt = {
        margin: 0.5,
        filename: `Safety_Plan_${formData.property_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' as const }
      };

      await html2pdf().set(opt).from(element).save();
      
      // Save to database
      const { error } = await supabase.from("safety_plans").insert({
        org_id: orgId,
        property_name: formData.property_name,
        roof_access_method: formData.roof_access_method || null,
        fall_protection_method: formData.fall_protection_method || null,
        crew_size: parseInt(formData.crew_size),
        equipment_list: formData.equipment_list.split(',').map(s => s.trim()).filter(Boolean),
        hazards_identified: formData.hazards_identified.split(',').map(s => s.trim()).filter(Boolean),
        emergency_contacts: {
          name: formData.emergency_contact_name || null,
          phone: formData.emergency_contact_phone || null,
        },
        plan_html: planHTML,
        pdf_url: null, // Could upload to storage bucket if needed
      });

      if (error) throw error;

      toast.dismiss(loadingToast);
      toast.success("Safety plan generated and saved!");
      onOpenChange(false);
      
      // Reset form
      setFormData({
        property_name: "",
        roof_access_method: "",
        fall_protection_method: "",
        crew_size: "",
        equipment_list: "",
        hazards_identified: "",
        emergency_contact_name: "",
        emergency_contact_phone: "",
      });
    } catch (error: any) {
      console.error("Error generating safety plan:", error);
      toast.dismiss(loadingToast);
      toast.error("Failed to generate safety plan: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generate OSHA Safety Plan</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="property_name">Property Name *</Label>
            <Input
              id="property_name"
              value={formData.property_name}
              onChange={(e) => setFormData({ ...formData, property_name: e.target.value })}
              placeholder="e.g., Tech Center South"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="roof_access_method">Roof Access Method</Label>
            <Select
              value={formData.roof_access_method}
              onValueChange={(value) => setFormData({ ...formData, roof_access_method: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select access method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Interior Stairwell">Interior Stairwell</SelectItem>
                <SelectItem value="Exterior Ladder">Exterior Ladder</SelectItem>
                <SelectItem value="Scaffold">Scaffold</SelectItem>
                <SelectItem value="Boom Lift">Boom Lift</SelectItem>
                <SelectItem value="Scissor Lift">Scissor Lift</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fall_protection_method">Fall Protection Method</Label>
            <Select
              value={formData.fall_protection_method}
              onValueChange={(value) => setFormData({ ...formData, fall_protection_method: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select fall protection" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Guardrail System">Guardrail System</SelectItem>
                <SelectItem value="Personal Fall Arrest System (PFAS)">Personal Fall Arrest System (PFAS)</SelectItem>
                <SelectItem value="Safety Net System">Safety Net System</SelectItem>
                <SelectItem value="Warning Line System">Warning Line System</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="crew_size">Crew Size *</Label>
            <Input
              id="crew_size"
              type="number"
              value={formData.crew_size}
              onChange={(e) => setFormData({ ...formData, crew_size: e.target.value })}
              placeholder="e.g., 5"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="equipment_list">Equipment List (comma-separated)</Label>
            <Textarea
              id="equipment_list"
              value={formData.equipment_list}
              onChange={(e) => setFormData({ ...formData, equipment_list: e.target.value })}
              placeholder="e.g., Harnesses, Lanyards, Anchor points, Ladders, Tools"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="hazards_identified">Identified Hazards (comma-separated)</Label>
            <Textarea
              id="hazards_identified"
              value={formData.hazards_identified}
              onChange={(e) => setFormData({ ...formData, hazards_identified: e.target.value })}
              placeholder="e.g., Working at heights, Slip/trip hazards, Electrical hazards, Weather exposure"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="emergency_contact_name">Emergency Contact Name</Label>
              <Input
                id="emergency_contact_name"
                value={formData.emergency_contact_name}
                onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                placeholder="Site supervisor"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emergency_contact_phone">Emergency Contact Phone</Label>
              <Input
                id="emergency_contact_phone"
                value={formData.emergency_contact_phone}
                onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                placeholder="555-0123"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleGenerateAndSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Generate & Download PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}